/**
 * Service-account / API-key admin hooks (P119.4).
 *
 * Wrap the admin CRUD at /v1/admin/service-accounts. Create + rotate
 * return the plaintext key ONCE (the UI surfaces it for copy + warns it
 * won't be shown again); everything else is keyless.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "shared/api/client";
import type {
  ServiceAccount,
  ServiceAccountCreated,
  ServiceAccountCreateIn,
  ServiceAccountPatchIn,
} from "shared/api/types";

const _BASE = "/v1/admin/service-accounts";

export const serviceAccountKeys = {
  list: ["service-accounts"] as const,
};

export function useServiceAccounts(opts?: {
  enabled?: boolean;
}): UseQueryResult<ServiceAccount[]> {
  return useQuery({
    queryKey: serviceAccountKeys.list,
    queryFn: () => api.get<ServiceAccount[]>(_BASE),
    enabled: opts?.enabled ?? true,
    staleTime: 15_000,
  });
}

export function useCreateServiceAccount(): UseMutationResult<
  ServiceAccountCreated,
  Error,
  ServiceAccountCreateIn
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body: ServiceAccountCreateIn) =>
      api.post<ServiceAccountCreated>(_BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceAccountKeys.list }),
  });
}

export function useRotateServiceAccount(): UseMutationResult<
  ServiceAccountCreated,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (id: string) =>
      api.post<ServiceAccountCreated>(`${_BASE}/${id}/rotate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceAccountKeys.list }),
  });
}

export function useUpdateServiceAccount(): UseMutationResult<
  ServiceAccount,
  Error,
  { id: string; body: ServiceAccountPatchIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ServiceAccountPatchIn }) =>
      api.patch<ServiceAccount>(`${_BASE}/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceAccountKeys.list }),
  });
}

export function useDeleteServiceAccount(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${_BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceAccountKeys.list }),
  });
}
